import { SK, btnCss, panelCss, titleCss, redHover, injectSkinCss } from './skin';

// MOD ACCESS (backtick, mod-flagged non-admin accounts only -- accounts.is_mod, hand-granted via D1, no UI to
// self-serve it). Deliberately narrow: THREE gameplay-cheat toggles and nothing else -- no roster, no search,
// no kick/ban/mute/teleport. Those stay gated server-side on OWNER_IDS / the X-Acct-Admin header (server/src/
// index.ts), completely untouched by this panel -- a mod account never gets that header and this class never
// sends a 'mod' socket message. See main.ts for the fly/god/res closures this shares with the admin ModPanel.
type Toggle = { label: string; get: () => boolean; toggle: () => void };

export class ModAccessPanel {
  private readonly root: HTMLDivElement;
  private open_ = false;
  private readonly toolBtns: Array<{ t: Toggle; el: HTMLButtonElement }> = [];

  constructor(private readonly tools: { fly: Toggle; god: Toggle; res: Toggle }) {
    injectSkinCss();
    this.root = document.createElement('div');
    this.root.dataset.ui = '1';
    this.root.style.cssText = css('position:fixed', 'inset:0', 'z-index:40', 'display:none', 'align-items:center', 'justify-content:center',
      `background:${SK.scrim}`, `font:${SK.fsBody} ${SK.font}`, `color:${SK.text}`);
    this.root.addEventListener('mousedown', (e) => { if (e.target === this.root) this.close(); });

    const panel = document.createElement('div');
    panel.style.cssText = css(panelCss('glass'), 'display:flex', 'flex-direction:column', 'gap:8px', 'padding:20px 24px', 'width:min(280px,90vw)',
      'box-shadow:0 24px 70px rgba(0,0,0,0.7)');

    const title = document.createElement('div');
    title.textContent = 'MOD ACCESS';
    title.style.cssText = css(titleCss(), 'margin-bottom:4px');
    panel.appendChild(title);

    for (const t of [this.tools.fly, this.tools.god, this.tools.res]) {
      const b = document.createElement('button');
      b.style.cssText = css(btnCss('secondary'), 'justify-content:flex-start'); // paint() recolors per state
      b.onclick = () => { t.toggle(); this.paint(); };
      panel.appendChild(b);
      this.toolBtns.push({ t, el: b });
    }

    const close = document.createElement('button');
    close.textContent = '✕ Close';
    close.style.cssText = css(btnCss('secondary', true), 'margin-top:4px');
    close.onclick = () => this.close();
    redHover(close);
    panel.appendChild(close);

    this.root.appendChild(panel);
    document.body.appendChild(this.root);
  }

  get isOpen(): boolean { return this.open_; }
  toggle(): void { this.open_ ? this.close() : this.show(); }
  show(): void {
    this.open_ = true;
    this.root.style.display = 'flex';
    try { document.exitPointerLock(); } catch { /* not locked */ }
    this.paint();
  }
  close(): void {
    this.open_ = false;
    this.root.style.display = 'none';
  }

  // Green = state-positive: ON; off is neutral.
  private paint(): void {
    for (const { t, el } of this.toolBtns) {
      const on = t.get();
      el.textContent = (on ? '⚡ ' : '') + t.label + ': ' + (on ? 'ON' : 'OFF');
      el.style.color = on ? SK.good : SK.dim;
      el.style.background = on ? SK.goodSoft : 'transparent';
      el.style.borderColor = on ? SK.good : SK.borderStrong;
    }
  }
}

function css(...lines: string[]): string { return lines.filter(Boolean).join(';'); }
